import { useEffect, useRef } from 'react';
import { usePusher } from '../../contexts/PusherContext';
import { useCompany } from '../../contexts/CompanyContext';
import { isV3PlanOverview } from '../../utils/planOverview';

interface CreditsUpdatedEvent {
  credits: number;
  resets_at: string | null;
}

/**
 * Bridge component that listens for 'credits-updated' Pusher events and
 * updates CompanyContext state accordingly.
 *
 * The company channel carries the credits the company itself owns. An
 * organisation-funded member additionally receives the balance they can
 * actually spend (company credits + their organisation allocation / pool)
 * on their own user channel. Once a user-channel figure has been seen it
 * wins: company-channel events are ignored for the rest of the session, so
 * the counter never falls back to the company-only figure regardless of the
 * order the two events arrive in. A user with no organisation membership
 * never receives a user-channel event and behaves exactly as before.
 *
 * Under personal billing v3 the same events also refresh the plan overview so
 * the usage level (fine / running low / used up) follows consumption live.
 *
 * Must be rendered inside both PusherProvider and CompanyProvider.
 */
const CreditsPusherListener: React.FC = () => {
  const pusherContext = usePusher(true);
  const companyChannel = pusherContext?.pusherChannel;
  const userChannel = pusherContext?.userChannel;
  const { setCredits, setResetsAt, planOverview, refreshPlanOverview } =
    useCompany();
  const userFigureSeen = useRef(false);
  const isV3 = isV3PlanOverview(planOverview);

  useEffect(() => {
    if (!companyChannel) return;

    const handleCompanyCreditsUpdated = (data: CreditsUpdatedEvent) => {
      console.log('Received credits-updated event (company):', data);
      if (userFigureSeen.current) return;
      setCredits(data.credits);
      setResetsAt(data.resets_at);
      if (isV3) refreshPlanOverview();
    };

    companyChannel.bind('credits-updated', handleCompanyCreditsUpdated);

    return () => {
      companyChannel.unbind('credits-updated', handleCompanyCreditsUpdated);
    };
  }, [companyChannel, setCredits, setResetsAt, isV3, refreshPlanOverview]);

  useEffect(() => {
    if (!userChannel) return;

    const handleUserCreditsUpdated = (data: CreditsUpdatedEvent) => {
      console.log('Received credits-updated event (user):', data);
      userFigureSeen.current = true;
      setCredits(data.credits);
      setResetsAt(data.resets_at);
      if (isV3) refreshPlanOverview();
    };

    userChannel.bind('credits-updated', handleUserCreditsUpdated);

    return () => {
      userChannel.unbind('credits-updated', handleUserCreditsUpdated);
    };
  }, [userChannel, setCredits, setResetsAt, isV3, refreshPlanOverview]);

  return null;
};

export default CreditsPusherListener;
